import { useEffect, useRef } from 'react';

import { Modal, ModalBody, ModalHeader, Text } from '@change/ds-core-ui';

import { useI18n, withI18nPlaceholders } from '@change-corgi/core-react/i18n';

import I18N_PLACEHOLDERS from '#config/i18n/placeholders/petitionDetails/petition-details.yml';

import type { Endorsement } from '#src/app/shared/components/endorsements';
import { getEndorsementCardElementId } from '#src/app/shared/utils/endorsements';

import { EndorsementsList } from '../EndorsementsList';

type Props = {
	isOpen: boolean;
	petitionId: string;
	onClose: () => void;
	setIsOpen: (isOpen: boolean) => void;
	endorsements: readonly Endorsement[];
	endorsementIdToScrollTo: string | null;
	onScrolledToEndorsement: () => void;
};

const EndorsementsModalComponent: React.FC<Props> = ({
	isOpen,
	petitionId,
	onClose,
	setIsOpen,
	endorsements,
	endorsementIdToScrollTo,
	onScrolledToEndorsement,
}) => {
	const { translate } = useI18n();
	const modalBodyRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		let timeoutId: number | undefined;

		if (isOpen && endorsementIdToScrollTo && modalBodyRef.current) {
			const scrollToEndorsement = () => {
				const element = modalBodyRef.current?.querySelector(
					`[id="${getEndorsementCardElementId(endorsementIdToScrollTo)}"]`,
				);
				if (element) {
					element.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
				onScrolledToEndorsement();
			};

			timeoutId = window.setTimeout(scrollToEndorsement, 100);
		}

		return () => {
			if (timeoutId !== undefined) {
				window.clearTimeout(timeoutId);
			}
		};
	}, [isOpen, endorsementIdToScrollTo, onScrolledToEndorsement]);

	return (
		<div>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
				onOpenChange={setIsOpen}
				className="z-10000"
				size="large"
				aria-label={translate('corgi.petition_details.endorsements.aria_label')}
				aria-modal="true"
			>
				<ModalHeader title={translate('corgi.petition.details.endorsements.heading')} />

				<ModalBody className="pt-6">
					<div ref={modalBodyRef} className="flex flex-col gap-6">
						<Text variant="body" size="large" className="text-typography-weak">
							{translate('corgi.petition.details.endorsements.subheading')}
						</Text>
						<EndorsementsList showAll endorsements={endorsements} petitionId={petitionId} />
					</div>
					<div>
						<Text variant="body" size="small" className="text-typography-weak">
							{translate('corgi.petition_details.endorsements.legal_disclaimer')}
						</Text>
					</div>
				</ModalBody>
			</Modal>
		</div>
	);
};

export const EndorsementsModal = withI18nPlaceholders(I18N_PLACEHOLDERS, EndorsementsModalComponent);
